import '@jack-henry/jh-ui/components/input/input.js';
import { applyCurrencyMask, formatCurrencyDecimals } from '@treasury/utils/';
import { validateValueByType } from '@treasury/utils/validators';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmFormControl, TmFormControlDefault } from '../forms';

type JhInputChanged = CustomEvent<string> & { target: { value: string } };
export type TmInputType =
    | 'text'
    | 'search'
    | 'password'
    | 'email'
    | 'tel'
    | 'url'
    | 'textarea'
    | 'alpha-numeric'
    | 'numeric'
    | 'ip'
    | 'currency';

const tagName = 'tm-input';

@customElement(tagName)
export class TmInput extends TmFormControl<string> implements TmFormControlDefault<string> {
    @property({ type: String })
    public placeholder?: string;

    @property({ type: String })
    public label?: string;

    @property({ type: Boolean })
    public showIndicator?: boolean = false;

    @property({ type: String })
    public helperText?: string;

    @property({ type: String })
    public errorText?: string;

    @property({ type: Number })
    public maxLength?: number;

    @property({ type: Number })
    public minLength?: number;

    @property()
    public type: TmInputType = 'text';

    @property({ type: String })
    public default?: string;

    @property({ type: String })
    public mask?: string;

    @property({ type: Function })
    public maskFunction?: (value: string) => string;

    @property({ type: String })
    public currencySymbol?: string = '$';

    @property({ type: Number })
    public currencyDecimals?: number = 2;

    @property({ type: Boolean })
    public disabled?: boolean = false;

    @property({ type: String })
    public size?: 'small' | 'medium' | 'large';

    @property({ type: Number })
    public cols?: number;

    @property({ type: Number })
    public rows?: number;

    @state()
    private isDirty: boolean = false;

    private readonly textInputRef = createRef<
        LitElement & { value: string; validity: { valid: boolean } }
    >();

    // Attach the dirty handler to the input after first update
    public firstUpdated() {
        const input = this.textInput;
        if (input) {
            input.addEventListener('input', this.handleInputDirty.bind(this));
        }
    }

    /**
     * Gets the current display value (with mask applied)
     */
    public getDisplayValue(): string {
        const currentValue = this.textInput?.value ?? '';
        return this.getMaskedValue(currentValue);
    }

    /**
     * Gets the current raw value (without mask)
     */
    public getRawValue(): string {
        const currentValue = this.textInput?.value ?? '';
        return this.getUnmaskedValue(currentValue);
    }

    /**
     * Sets the value with proper masking
     */
    public setValue(value: string): void {
        const maskedValue = this.getMaskedValue(value);
        if (this.textInput) {
            this.textInput.value = maskedValue;
        }
        this.changeValue(this.getUnmaskedValue(maskedValue));
    }

    protected validate() {
        const result = validateValueByType(this.type, this.textInput?.value ?? '', {
            maxLength: this.maxLength,
            minLength: this.minLength,
            required: this.required,
        });

        const isValid = this.textInput?.validity?.valid ?? result.isValid ?? true;

        if (isValid) {
            return true;
        }

        if (result.errorMessage) {
            return [result.errorMessage];
        }

        return ['Please enter a valid value'];
    }

    protected render() {
        const initialValue = this.initial?.toString() ?? '';
        const displayValue = this.getMaskedValue(initialValue);
        const invalid = this.isDirty ? !this.validate() : false;
        const errorText = this.errorMessages?.join('\r\n');

        return html`
            <jh-input
                ?required=${this.required}
                .showIndicator=${this.showIndicator}
                ?disabled=${this.disabled}
                ${ref(this.textInputRef)}
                @input=${(e: JhInputChanged) => this.handleChange(e)}
                @blur=${() => this.handleBlur()}
                label=${this.label ?? ''}
                .placeholder=${this.placeholder ?? ''}
                .value=${displayValue}
                .helperText=${this.helperText ?? ''}
                .errorText=${errorText}
                type=${this.type}
                .maxlength=${`${this.maxLength ?? undefined}`}
                .minlength=${`${this.minLength ?? undefined}`}
                ?invalid=${invalid}
                size=${ifDefined(this.size)}
                cols=${ifDefined(this.cols)}
                rows=${ifDefined(this.rows)}
            ></jh-input>
        `;
    }

    private handleInputDirty() {
        if (!this.isDirty) {
            this.isDirty = true;
        }
    }

    /**
     * Applies a mask to the input value
     * @param value - The raw input value
     * @param mask - The mask pattern (e.g., "(999) 999-9999" for phone)
     * @returns The masked value
     */
    private applyMask(value: string, mask: string): string {
        if (!value || !mask) {
            return value;
        }

        let result = '';
        let valueIndex = 0;

        for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
            const maskChar = mask[i];
            const valueChar = value[valueIndex];

            switch (maskChar) {
                case '9': // Digit only
                    if (/\d/.test(valueChar)) {
                        result += valueChar;
                        valueIndex++;
                    }
                    break;
                case 'A': // Letter only
                    if (/[a-zA-Z]/.test(valueChar)) {
                        result += valueChar;
                        valueIndex++;
                    }
                    break;
                case 'X': // Any character
                    result += valueChar;
                    valueIndex++;
                    break;
                case '*': // Alphanumeric
                    if (/[a-zA-Z0-9]/.test(valueChar)) {
                        result += valueChar;
                        valueIndex++;
                    }
                    break;
                default: // Literal character
                    result += maskChar;
                    if (valueChar === maskChar) {
                        valueIndex++;
                    }
                    break;
            }
        }

        return result;
    }

    /**
     * Gets the masked value using either the mask pattern or custom mask function
     */
    private getMaskedValue(value: string): string {
        if (this.maskFunction) {
            return this.maskFunction(value);
        }
        if (this.mask) {
            return this.applyMask(value, this.mask);
        }
        if (this.type === 'ip') {
            return this.applyIpMask(value);
        }
        if (this.type === 'currency') {
            return applyCurrencyMask(value, {
                symbol: this.currencySymbol,
                decimals: this.currencyDecimals,
            });
        }
        return value;
    }

    /**
     * Applies IP address masking with flexible dot insertion
     * @param value - The raw input value
     * @returns The masked IP address
     */
    private applyIpMask(value: string): string {
        if (!value) {
            return value;
        }

        // Remove all non-digit and non-dot characters first
        let cleanValue = value.replace(/[^\d.]/g, '');

        // No consecutive dots
        cleanValue = cleanValue.replace(/\.{2,}/g, '.');

        // No leading dot.
        if (cleanValue.startsWith('.')) {
            cleanValue = cleanValue.substring(1);
        }

        // Automatically add dots after 3 digits in a segment
        const segments = cleanValue.split('.');
        const newSegments = segments.flatMap((segment, index) => {
            if (index < 3 && segment.length > 3) {
                const parts = [];
                let currentSegment = segment;
                while (currentSegment.length > 3) {
                    parts.push(currentSegment.substring(0, 3));
                    currentSegment = currentSegment.substring(3);
                }
                parts.push(currentSegment);
                return parts;
            }
            return segment;
        });

        // Limit to 4 segments and 3 digits per segment
        const processedSegments = newSegments.slice(0, 4).map(segment => segment.substring(0, 3));

        const result = processedSegments.join('.');

        return result;
    }

    /**
     * Removes mask characters from a value to get the raw input
     */
    private getUnmaskedValue(value: string): string {
        if (!this.mask && !this.maskFunction && this.type !== 'ip' && this.type !== 'currency') {
            return value;
        }

        // For IP addresses, return the masked value as-is since it's already formatted
        if (this.type === 'ip') {
            return value;
        }

        // For currency, return the numeric value without formatting
        if (this.type === 'currency') {
            return value.replace(/[^\d.]/g, '');
        }

        // For simple masks, remove non-alphanumeric characters
        if (this.mask) {
            return value.replace(/[^a-zA-Z0-9]/g, '');
        }

        // For custom mask functions, we can't easily reverse them
        // so we'll return the original value
        return value;
    }

    private get textInput() {
        return this.textInputRef.value;
    }

    private handleBlur() {
        if (this.type === 'currency' && this.textInput) {
            const currentValue = this.textInput.value;
            this.textInput.value = formatCurrencyDecimals(currentValue, {
                decimals: this.currencyDecimals,
            });
        }
    }

    private handleChange(e: JhInputChanged) {
        const rawValue = e.target?.value ?? '';
        const maskedValue = this.getMaskedValue(rawValue);

        // Store the unmasked value for validation and form submission
        const unmaskedValue = this.getUnmaskedValue(maskedValue);
        this.changeValue(unmaskedValue);

        // Update the input with the masked value if different
        if (this.textInput && maskedValue !== rawValue) {
            this.textInput.value = maskedValue;
        }
    }

    private isOptionalEmpty() {
        return !this.required && (this.textInput?.value ?? '' === '');
    }

    private isValidLength() {
        if (this.maxLength && (this.textInput?.value?.length ?? 0) > this.maxLength) {
            return false;
        }
        if (this.minLength && (this.textInput?.value?.length ?? this.minLength) < this.minLength) {
            return false;
        }
        return true;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmInput;
    }
}
